// 🔧 entry_generator.js
// ✅ Purpose: Generic industry entry-generation dispatcher for the LGV Legacy Experience Factory
// ✅ Features:
// - Provides one entry-generation API for Demo Engine / Experience Factory builders
// - Routes entry generation by registered industry key
// - Preserves the existing Car Show entry generator without changing its behavior
// - Prevents generic Entry Builder code from importing industry-specific generators directly
// - Provides the extension point for SAP AI Agents Challenge and future Experience Factory industries
// - Throws clearly for unsupported industries instead of silently generating incorrect demo content
// - Fully block-commented for clarity and future devs

import { generateCarShowEntries } from "./car_show_entry_generator";
import { generateSapAiAgentsChallengeEntries } from "./sap_ai_agents_challenge_entry_generator";
import { getIndustryTemplate } from "../industries";
import { generateImagesForEntry } from "./image_generator";

/**
 * ✅ Generate entries for one registered Experience Factory industry.
 *
 * Generic builders should call this function rather than importing
 * industry-specific entry generators themselves.
 */
export const generateEntriesForIndustry = ({
  industryKey = "car_show",
  count = 10,
  categories = [],
} = {}) => {
  const industry =
    getIndustryTemplate(industryKey);

  if (!industry?.key) {
    throw new Error(
      `Experience Factory industry "${industryKey}" could not be resolved.`
    );
  }

  // =====================================================
  // Car Show
  // =====================================================
  // Preserve the existing generator exactly as the first
  // registered implementation.
  // =====================================================

  if (industry.key === "car_show") {
    return generateCarShowEntries({
      count,
      categories,
    });
  }

  // =====================================================
  // SAP AI Agents Challenge
  // =====================================================
  // This purpose-built Experience uses a fixed collection of
  // thirteen fictional partner teams defined by its manifest.
  // =====================================================

  if (industry.key === "sap_ai_agents_challenge") {
    return generateSapAiAgentsChallengeEntries({
      count,
      categories,
    });
  }

  if (industry.entries?.length) {
    return Array.from({ length: count }, (_, index) => {
      const sourceEntry = industry.entries[index % industry.entries.length];
      const cycle = Math.floor(index / industry.entries.length) + 1;
      const baseTitle = sourceEntry.title || sourceEntry.name || `Entry ${index + 1}`;
      const title = cycle > 1 ? `${baseTitle} ${cycle}` : baseTitle;
      const imageData = generateImagesForEntry({
        entry: { title },
        industryKey: industry.key,
      });

      return {
        ...sourceEntry,
        title,
        ownerName: sourceEntry.ownerName || `Example Participant ${index + 1}`,
        entryType: sourceEntry.entryType || industry.entryLabel || "Entry",
        participantType:
          sourceEntry.participantType ||
          industry.experience?.participantType ||
          industry.entryLabel ||
          "Participant",
        categoryName:
          sourceEntry.categoryName ||
          categories[index % Math.max(categories.length, 1)] ||
          "General",
        displayOrder: index + 1,
        imgUrl: sourceEntry.imgUrl || imageData.thumbnail,
        imageUrl: sourceEntry.imageUrl || imageData.thumbnail,
        images:
          Array.isArray(sourceEntry.images) && sourceEntry.images.length > 0
            ? sourceEntry.images
            : imageData.gallery,
        imagePlaceholder: imageData.placeholder,
        imageSource: imageData.source,
        imageAltText: imageData.altText,
      };
    });
  }

  // =====================================================
  // Unsupported Industry Guard
  // =====================================================
  // Do not silently fall back to Car Show content for a
  // recognized future industry.
  // =====================================================

  throw new Error(
    `Experience Factory entry generation is not implemented for industry "${industry.key}".`
  );
};
